import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Collapse,
  Flex,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonTable from 'components/commonTable/CommonTable';
import CommonPagination from 'components/commonPagination/CommonPagination';
import ExportButton from 'components/commonExport/ExportButton';
import { useDispatch, useSelector } from 'react-redux';
import * as dateFns from 'date-fns';
import useListState from 'hooks/useListState';
import { ListOperationLogData } from 'reactRedux/thunks/operationLogThunk/operationLogThunk';
import OperationLogFilter, {
  OUTCOME_OPTIONS,
  ROUTE_OPTIONS,
} from './OperationLogFilter';

const OUTCOME_COLOR = {
  success: 'green',
  failed: 'red',
  skipped: 'orange',
};

const labelFor = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value;

/**
 * RAT-1760 AC-6: one changed field, rendered as old -> new.
 *
 * Values are shown verbatim, per Decision 4 (no redaction). Long values are
 * clamped visually rather than truncated in the data, so the export still carries
 * the whole value.
 */
const ChangeRow = ({ change }) => (
  <Flex fontSize="12px" gap="6px" wrap="wrap" py="2px">
    <Text fontWeight="600" whiteSpace="nowrap">
      {change?.label}:
    </Text>
    <Text color="red.500" wordBreak="break-word">
      {change?.from ?? 'empty'}
    </Text>
    <Text color="gray.400">to</Text>
    <Text color="green.600" wordBreak="break-word">
      {change?.to ?? 'empty'}
    </Text>
  </Flex>
);

ChangeRow.propTypes = { change: PropTypes.object };

/**
 * The Changes cell.
 *
 * Collapsed to a count by default because a merchant has 42 loggable fields and
 * an expanded diff on every row would bury the rest of the table. Migrated
 * history (AC-12) has no structured diff - the RAT-1474 sheet stored both sides
 * as one text blob - so it renders that text as-is instead of an empty change
 * list, which would read as "nothing changed".
 */
const ChangesCell = ({ entry }) => {
  const { isOpen, onToggle } = useDisclosure();

  if (entry?.isMigrated) {
    return (
      <Box>
        <Text
          fontSize="12px"
          color="blue.500"
          cursor="pointer"
          onClick={onToggle}
        >
          {isOpen ? 'Hide' : 'Show'} migrated entry
        </Text>
        <Collapse in={isOpen} animateOpacity>
          <Text fontSize="11px" whiteSpace="pre-wrap" color="gray.600" mt="4px">
            {entry?.legacyPayload || 'No detail recorded'}
          </Text>
        </Collapse>
      </Box>
    );
  }

  if (!entry?.changeCount) {
    return (
      <Text fontSize="12px" color="gray.400">
        {entry?.reason ? 'No change (see reason)' : 'No fields recorded'}
      </Text>
    );
  }

  return (
    <Box>
      <Text
        fontSize="12px"
        color="blue.500"
        cursor="pointer"
        onClick={onToggle}
      >
        {entry.changeCount} field{entry.changeCount > 1 ? 's' : ''} changed
      </Text>
      <Collapse in={isOpen} animateOpacity>
        <Box mt="4px">
          {(entry?.changes || []).map((change, index) => (
            <ChangeRow key={`${change?.field}-${index}`} change={change} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

ChangesCell.propTypes = { entry: PropTypes.object };

/**
 * RAT-1760: the operation log (AC-6, AC-7, AC-8).
 *
 * Rendered standalone as its own portal section, and reused pre-filtered from a
 * merchant's History tab by passing `entityId` - same component, one extra entry
 * point, so the two views can never disagree about how a change is presented.
 */
const OperationLogList = ({ entityId, entityType, embedded }) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const dispatch = useDispatch();
  const addDays = dateFns.addDays;

  // `embedded` means "this is one record's history". If the id never arrived,
  // fetching anyway would render every record's history inside that record's
  // page - unscoped data under a heading that promises the opposite, which is
  // worse than showing nothing.
  const scopedToOneRecord = Number.isFinite(entityId);
  const misconfigured = embedded && !scopedToOneRecord;

  // Scalar filters live in the URL so opening a record from the log and coming
  // back restores the exact view; the date range stays local because a date-object
  // range is not URL-shaped (same split as EInvoiceList).
  const { params, setParam, debouncedParams, page, setPage } = useListState({
    defaults: { search: '', route: '', outcome: '', limit: 25 },
    debounceKeys: ['search'],
    prefix: embedded ? 'log_' : '',
  });

  const [dateRange, setDateRange] = useState([
    {
      endDate: new Date(),
      startDate: addDays(new Date(), -6),
      key: 'selection',
    },
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [calendarFilterTitle, setCalendarFilterTitle] = useState('Last 7 days');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 7 days');

  const { isLoading, operationLogData } = useSelector(
    (state) => state.OperationLogSlice,
  );

  // One filter object, used for both the fetch and the export, so the file can
  // never cover a different set than the screen (AC-7). Empty values are dropped
  // rather than sent as blanks.
  const exportFilters = useMemo(() => {
    const filters = {
      startDate: dateFns.format(dateRange[0]?.startDate, 'yyyy-MM-dd'),
      endDate: dateFns.format(dateRange[0]?.endDate, 'yyyy-MM-dd'),
    };
    if (entityType) filters.entityType = entityType;
    if (Number.isFinite(entityId)) filters.entityId = entityId;
    if (params.route) filters.route = params.route;
    if (params.outcome) filters.outcome = params.outcome;
    if (debouncedParams.search) filters.search = debouncedParams.search;
    return filters;
  }, [
    entityType,
    entityId,
    params.route,
    params.outcome,
    debouncedParams.search,
    dateRange,
  ]);

  const getOperationLogList = useCallback(() => {
    if (misconfigured) {
      return;
    }
    dispatch(
      ListOperationLogData({
        ...exportFilters,
        page,
        limit: params.limit,
      }),
    );
  }, [dispatch, exportFilters, page, params.limit, misconfigured]);

  // useListState resets the page when a URL-backed filter changes; the date range
  // is local, so it needs the reset done here. Without it, narrowing the range
  // while on page 7 shows an empty table that reads as "no history".
  useEffect(() => {
    setPage(1);
  }, [dateRange, setPage]);

  useEffect(() => {
    getOperationLogList();
  }, [getOperationLogList]);

  const handleApply = () => {
    setDateRange(tempDateRange);
    setCalendarFilterTitle(tempCalendarFilterTitle);
    setShowCalendar(false);
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => {
    // Named so the table's header renderer is not an anonymous component.
    const header = (label) => {
      const HeaderCell = () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          {label}
        </Text>
      );
      return HeaderCell;
    };

    const cell = (value) => (
      <Text color={textColor} fontSize="sm" fontWeight="500">
        {value}
      </Text>
    );

    const base = [
      columnHelper.accessor((row) => row?.createdAt, {
        id: 'createdAt',
        header: header('Date'),
        cell: (info) =>
          cell(
            info.getValue()
              ? dateFns.format(new Date(info.getValue()), 'dd/MM/yyyy HH:mm:ss')
              : '-',
          ),
      }),
    ];

    // The record column is noise on a merchant's own History tab - every row is
    // that merchant - so it is only shown on the standalone section.
    if (!embedded) {
      base.push(
        columnHelper.accessor((row) => row?.entityLabel, {
          id: 'entityLabel',
          header: header('Record'),
          cell: (info) => (
            <Box>
              {cell(info.getValue() || '-')}
              <Text fontSize="11px" color="gray.400">
                {info.row.original?.entityType} #{info.row.original?.entityId}
              </Text>
            </Box>
          ),
        }),
      );
    }

    base.push(
      columnHelper.accessor((row) => row?.action, {
        id: 'action',
        header: header('Action'),
        cell: (info) => cell(info.getValue()),
      }),
      columnHelper.accessor((row) => row?.route, {
        id: 'route',
        header: header('Route'),
        cell: (info) => cell(labelFor(ROUTE_OPTIONS, info.getValue())),
      }),
      columnHelper.accessor((row) => row?.outcome, {
        id: 'outcome',
        header: header('Outcome'),
        cell: (info) => (
          <Box>
            <Badge
              colorScheme={OUTCOME_COLOR[info.getValue()] || 'gray'}
              borderRadius="6px"
              px="8px"
            >
              {labelFor(OUTCOME_OPTIONS, info.getValue())}
            </Badge>
            {info.row.original?.reason && (
              <Text fontSize="11px" color="red.500" mt="2px">
                {info.row.original.reason}
              </Text>
            )}
          </Box>
        ),
      }),
      columnHelper.accessor((row) => row?.actorLabel, {
        id: 'actorLabel',
        header: header('Changed By'),
        cell: (info) => (
          <Box>
            {cell(info.getValue() || '-')}
            {info.row.original?.runId && (
              <Text fontSize="11px" color="gray.400">
                run {info.row.original.runId}
              </Text>
            )}
          </Box>
        ),
      }),
      columnHelper.accessor((row) => row?.changeCount, {
        id: 'changes',
        header: header('Changes'),
        cell: (info) => <ChangesCell entry={info.row.original} />,
      }),
    );

    return base;
  }, [columnHelper, textColor, embedded]);

  if (misconfigured) {
    return (
      <Text fontSize="sm" color="gray.500">
        No record selected, so there is no history to show.
      </Text>
    );
  }

  return (
    // The standalone page sits directly under the fixed portal header, so it
    // needs the same top padding every other top-level list uses - without it
    // the filter row and the Export button render behind the header, where they
    // are invisible and unclickable. The embedded copy is already inside a card
    // in the page flow and must not get the offset.
    <Box pt={embedded ? 0 : { base: '60px', md: '80px', xl: '80px' }}>
      <Flex
        justify="space-between"
        align="flex-end"
        wrap="wrap"
        gap="12px"
        w="100%"
      >
        <OperationLogFilter
          search={params.search}
          setSearch={(value) => setParam('search', value)}
          route={params.route}
          setRoute={(value) => setParam('route', value)}
          outcome={params.outcome}
          setOutcome={(value) => setParam('outcome', value)}
          setDateRange={setDateRange}
          tempDateRange={tempDateRange}
          setTempDateRange={setTempDateRange}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          calendarFilterTitle={calendarFilterTitle}
          setCalendarFilterTitle={setCalendarFilterTitle}
          tempCalendarFilterTitle={tempCalendarFilterTitle}
          setTempCalendarFilterTitle={setTempCalendarFilterTitle}
          handleApply={handleApply}
        />
        {/* Matches the filter row's own bottom margin so the two sit on the
            same baseline, and goes full width on a phone like every other
            list page's primary action. */}
        <Box mb={4} width={{ base: '100%', md: 'auto' }}>
          <ExportButton
            entity="operation-log"
            title="Export Operation Log"
            currentFilters={exportFilters}
            filteredTotal={operationLogData?.total}
          />
        </Box>
      </Flex>

      <CommonTable
        columns={columns}
        data={operationLogData?.entries || []}
        isLoading={isLoading}
        keyValue="id"
        dynamicWidth={{
          createdAt: '160px',
          entityLabel: '200px',
          action: '100px',
          route: '150px',
          outcome: '160px',
          actorLabel: '180px',
          changes: '320px',
        }}
      />

      <CommonPagination
        totalCount={operationLogData?.total || 0}
        activePage={page}
        pageCount={Math.ceil(
          (operationLogData?.total || 0) /
            (operationLogData?.limit || params.limit),
        )}
        onPageChange={(selected) => setPage(selected)}
        itemsPerPage={operationLogData?.limit || params.limit}
        onItemsPerPageChange={(size) => setParam('limit', size)}
      />
    </Box>
  );
};

OperationLogList.propTypes = {
  /** Pre-filter to one record, e.g. a merchant's own History tab. */
  entityId: PropTypes.number,
  entityType: PropTypes.string,
  /** Hides the Record column when every row is the same record. */
  embedded: PropTypes.bool,
};

export default OperationLogList;
